import { ClosedPostNotice } from "../components/ClosedPostNotice";
import { Link } from "wouter";
import { Loader2 } from "lucide-react";
import { DetailNotFound } from "../components/DetailStates";
import { ProjectBiddingDetailMobile } from "../components/ProjectBiddingDetailMobile";
import { ConversationForm, AwardedConversationNotice } from "./project-detail/ProjectConversation";
import { useProjectDetail } from "./project-detail/useProjectDetail";
import { ProjectDesktopDetail } from "./project-detail/ProjectDesktopDetail";
import { ProjectDetailDialogs } from "./project-detail/ProjectDetailDialogs";

export default function ProjectDetail() {
  const model = useProjectDetail();
  const { t, navigate, user, id, item, loadState, setContactOpen, isMobileViewport, projectContract, err, setShowAllFiles, attachments, questions, p, loadExtras, isOwner, myBid, canMessageClient, awarded, biddingClosed, canEditBid, openBidComposer, downloadBidDocument } = model;
  if (loadState === "loading") {
    return (
      <div className="container-x py-24 text-center text-mut">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-navy" />
        Loading project…
      </div>
    );
  }

  if (!p) {
    return (
      <DetailNotFound
        title={loadState === "unavailable" ? "Project temporarily unavailable" : "Project not found"}
        description={loadState === "unavailable" ? "We couldn’t load this project right now. Please try again shortly." : undefined}
        backHref="/marketplace?tab=Project%20Bidding"
        backLabel="Back to Project Bidding"
      />
    );
  }

  const statusChip = awarded
    ? <span className="chip bg-brand-gold/15 text-[#8a5a00]">{t("activity.status.awarded")}</span>
    : biddingClosed ? <span className="chip bg-blue-50 text-blue-700">Shortlisting</span>
    : <span className="chip bg-brand-green/10 text-brand-greendark">Open for Bidding</span>;
  const agreedCompletion = projectContract?.offerStatus === "accepted" ? projectContract.proposedCompletionDate : null;
  // [projects.timeline](../../../docs/product/projects.md#projects-timeline): early means completion before the agreed Philippine calendar day.
  // Only the saved completion instant establishes this; missing dates do not.
  const completedEarly = projectContract?.status === "completed" && agreedCompletion
    && typeof projectContract.completedAt === "string"
    && Date.parse(projectContract.completedAt) < Date.parse(`${agreedCompletion}T00:00:00+08:00`);
  const completionState = projectContract?.status === "cancelled" ? "cancelled" : completedEarly ? "early" : "future";
  const completionNote = completionState === "cancelled" ? "Cancelled" : completedEarly ? "Finished early" : undefined;
  const ownerTimeline: Array<{ label: string; date: string; state: string; note?: string }> = [
    { label: "Project Posted", date: item?.createdAt ? new Date(item.createdAt).toLocaleDateString("en-PH", { dateStyle: "medium" }) : p.timeline[0]?.date || "—", state: "done" },
    { label: "Bidding deadline", date: p.deadline, state: biddingClosed ? "done" : "current" },
    { label: "Shortlisting", date: awarded ? "Completed" : "Client reviewing bids", state: awarded ? "done" : biddingClosed ? "current" : "future" },
    { label: "Awarding", date: awarded ? "Provider selected" : "To be decided", state: awarded ? "done" : "future" },
    ...(p.targetCompletionDate ? [{ label: "Client target completion", date: p.targetCompletionDate, state: "future" }] : []),
    ...(agreedCompletion ? [{ label: "Agreed completion", date: agreedCompletion, state: completionState, note: completionNote }] : []),
  ];

  const view = { ...model, p, statusChip, ownerTimeline };
  return (<>
    <div className="container-x pt-4"><ClosedPostNotice post={item || {}} /></div>
      {isMobileViewport && !isOwner && <ProjectBiddingDetailMobile
        project={p}
        signedIn={!!user}
        currentUserId={user?.id}
        canEditBid={canEditBid}
        biddingClosed={biddingClosed}
        awarded={awarded}
        myBid={myBid}
        attachments={attachments}
        questions={questions}
        questionForm={user
          ? <ConversationForm projectId={id} isOwner={false} onSent={loadExtras} />
          : <div className="mt-3 border-t border-line pt-3 text-sm text-mut"><button type="button" onClick={() => navigate("/login")} className="font-semibold text-brand-red">Sign in</button> to ask a project question.</div>}
        questionClosedNotice={<AwardedConversationNotice isOwner={false} bidStatus={myBid?.status} />}
        error={err}
        onOpenBid={openBidComposer}
        onSignIn={() => navigate("/login")}
        onMessage={() => setContactOpen(true)}
        canMessage={canMessageClient}
        onViewAllFiles={() => setShowAllFiles(true)}
        onDownloadBidDocument={(bidId, document) => { void downloadBidDocument(bidId, document); }}
      />}
      {projectContract?.negotiationId && <div className="container-x mt-4"><div role="status" className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-navy"><strong>{t("projectAgreement.negotiation")}</strong><p className="mt-1">The award is recorded. Review the current proposal in Messages; acceptance, signatures and funding are separate steps.</p><Link href={`/messages/${encodeURIComponent(projectContract.threadId)}?agreement=${encodeURIComponent(projectContract.negotiationId)}`} className="btn-outline mt-3 min-h-11 px-3 text-sm">{t("projectAgreement.messages")}</Link></div></div>}
      {(!isMobileViewport || isOwner) && <ProjectDesktopDetail view={view} />}

      <ProjectDetailDialogs view={view} />
    </>);
}
